import { useEffect, useState } from "react"
import { CopyRightText } from "@/components/custom/CopyRightText"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { PasswordInput } from "@/components/ui/password-input"
import { TCSLink } from "@/components/custom/Link/TCSLink"
import { Auth } from "@/API/Auth"
import { ResponseMessageBox } from "@/components/custom/ResponseMessageBox/ResponseMessageBox"
import { useNavigate, useLocation } from "react-router-dom"
import { useCookies } from "react-cookie"
import { AuthSchema } from "@/constants/auth/auth"
import { AuthMessage } from "@/constants/auth/auth"
import { useDispatch } from 'react-redux'
import { AppDispatch } from "@/redux"
import { userActions } from "@/redux/slice/userSlice"
import { LoginForm } from "@/types/auth"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Card,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"

const useQuery = () => {
  return new URLSearchParams(useLocation().search)
}

export const Login = () => {
  const dispatch: AppDispatch = useDispatch()
  const navigate = useNavigate()
  const query = useQuery()
  const [, setCookie] = useCookies(['tcsAccessToken', 'tcsRefreshToken'])
  const [formMessage, setFormMessage] = useState('')

  const form = useForm<LoginForm>({
    resolver: zodResolver(AuthSchema.login),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  useEffect(() => {
    setFormMessage('')
  }, [form])

  const onLoginSubmit = async (
    values: LoginForm
  ) => {
    const response = await Auth.login(values)

    if (!response.data && response.err) {
      setFormMessage(response.err)
    }
    if (response.data) {
      // set token
      setCookie('tcsRefreshToken', response.data.refreshToken.token, {
        domain: document.location.hostname,
        path: '/',
        expires: new Date(response.data.refreshToken.expiresAt)
      })
      setCookie('tcsAccessToken', response.data.accessToken.token, {
        domain: document.location.hostname,
        path: '/',
        expires: new Date(response.data.accessToken.expiresAt)
      })
      dispatch(userActions.setUserToken(response.data.accessToken.token))
      setFormMessage('')
      form.reset({
        email: "",
        password: "",
      })
      const returnTo = query.get('returnTo')
      if (returnTo) {
        navigate(returnTo)
      } else {
        navigate('/router')
      }
    }
  }

  useEffect(() => {
    // if we are coming from reset success, display a msg
    const reset = query.get('reset')
    const invite = query.get('invite')
    if (reset === 'success') {
      setFormMessage(AuthMessage.PASSWORD_RESET_SUCCESSFUL)
    }
    if (invite === 'success') {
      setFormMessage(AuthMessage.INVITATION_ACCEPTED)
    }
    if (invite === 'error') {
      setFormMessage(AuthMessage.INVITATION_ERROR)
    }
  }, [query])

  return <div className="bg-opacity-50 h-screen py-20 flex flex-col justify-center items-center">
    <Card className="auth-card w-sm flex-column p-10">
      <CardHeader className="mb-12">
        <CardTitle>The Camp Stack</CardTitle>
      </CardHeader>

      {/* Response Message */}
      {formMessage && <ResponseMessageBox messageTitle={formMessage} />}

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onLoginSubmit)}
          className="space-y-8"
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  className="flex justify-start mb-2 font-semibold text-primary"
                  isRequired
                >
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input
                    hasError={!!form.formState.errors.email}
                    type="email"
                    placeholder="john@example.com"
                    {...field}
                  />
                </FormControl>
                <FormMessage
                  showIcon={true}
                  className="flex justify-start"
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  className="flex justify-start font-semibold text-primary"
                  isRequired
                >
                  Password
                </FormLabel>
                <FormControl>
                  <PasswordInput
                    hasError={!!form.formState.errors.password}
                    placeholder="********"
                    {...field}
                  />
                </FormControl>
                <FormMessage
                  showIcon={true}
                  className="flex justify-start"
                />
              </FormItem>
            )}
          />
          <TCSLink
            linkTo='/forgot-password'
            linkText="Forgot Password"
            className="flex justify-start items-center h-1"
          />
          <Button type="submit" className="w-full mb-2 font-semibold">
            Log In
          </Button>
        </form>
      </Form>

      <CardFooter className="pt-6 flex justify-center">
        <p className="text-sm pr-1 poppins-extralight">Don’t have an account?</p>
        <TCSLink
          linkTo='/signup'
          linkText="Sign up here"
        />
      </CardFooter>
    </Card>
    <CopyRightText />
  </div>
}
